import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Dialog,
    DialogContent,
    Box,
    Button,
    Typography,
    IconButton,
    Card,
    CardContent,
    Grid,
    Chip,
    Fade,
    Slider
} from '@mui/material';
import { Close, CalendarToday, AccessTime, PlayArrow, Pause, VolumeUp, VolumeOff } from '@mui/icons-material';
import OpenHouseRegistrationForm from './forms/OpenHouseRegistrationForm';

const MODAL_STAGES = {
    VIDEO: 'video',
    OPEN_HOUSE_LIST: 'openHouseList',
    REGISTRATION_FORM: 'registrationForm'
};

const LOCAL_STORAGE_KEY = 'mga-video-modal-last-shown';

const VideoModal = ({ 
    open, 
    onClose, 
    events = [], 
    timeZone = 'Eastern Time',
    openHouseEmail = 'info@macgillisglobalacademy.net',
    cooldownDays = 7,
    isFromCornerPopup = false
}) => {
    const navigate = useNavigate();
    const [currentStage, setCurrentStage] = useState(MODAL_STAGES.VIDEO);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const videoRef = useRef(null);
    
    // Video control states
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const [cooldownSet, setCooldownSet] = useState(false);

    const handleVideoEnded = () => {
        setIsPlaying(false);
        setCurrentStage(MODAL_STAGES.OPEN_HOUSE_LIST);
    };

    const setCooldown = () => {
        if (!cooldownSet) {
            localStorage.setItem(LOCAL_STORAGE_KEY, Date.now().toString());
            setCooldownSet(true);
            console.log('Video modal cooldown set - popup will not appear for 7 days');
        }
    };

    const handlePlayPause = () => {
        if (!videoRef.current) return;
        
        if (isPlaying) {
            videoRef.current.pause();
        } else {
            videoRef.current.play();
            // Set cooldown when video starts playing
            setCooldown();
        }
        setIsPlaying(!isPlaying);
    };

    const handleTimeUpdate = () => {
        if (!videoRef.current) return;
        setCurrentTime(videoRef.current.currentTime);
    };

    const handleLoadedMetadata = () => {
        if (!videoRef.current) return;
        setDuration(videoRef.current.duration);
        
        // If opened from corner popup, reset video and autoplay with sound
        if (isFromCornerPopup) {
            videoRef.current.currentTime = 0;
            videoRef.current.muted = false;
            setIsMuted(false);
            setIsPlaying(true);
            // Start playing immediately
            videoRef.current.play().catch(console.error);
            // Set cooldown since video is starting to play
            setCooldown();
        }
    };

    const handleSeek = (event, newValue) => {
        if (!videoRef.current) return;
        const seekTime = (newValue / 100) * duration;
        videoRef.current.currentTime = seekTime;
        setCurrentTime(seekTime);
    };

    const handleVolumeChange = (event, newValue) => {
        const newVolume = newValue / 100;
        setVolume(newVolume);
        if (videoRef.current) {
            videoRef.current.volume = newVolume;
        }
        setIsMuted(newVolume === 0);
    };

    const handleMuteToggle = () => {
        if (!videoRef.current) return;
        
        if (isMuted) {
            videoRef.current.volume = volume;
            setIsMuted(false);
        } else {
            videoRef.current.volume = 0;
            setIsMuted(true);
        }
    };

    const formatTime = (time) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const handleRegisterClick = (event) => {
        setSelectedEvent(event);
        setCurrentStage(MODAL_STAGES.REGISTRATION_FORM);
    };

    const handleBackToList = () => {
        setSelectedEvent(null);
        setCurrentStage(MODAL_STAGES.OPEN_HOUSE_LIST);
    };

    const handleRegistrationSuccess = () => {
        setTimeout(() => {
            onClose();
        }, 3000);
    };

    const handleClose = () => {
        setCurrentStage(MODAL_STAGES.VIDEO);
        setSelectedEvent(null);
        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
        }
        // Reset video control states
        setIsPlaying(false);
        setCurrentTime(0);
        setShowControls(true);
        setCooldownSet(false);
        onClose();
    };


    const getUpcomingEvents = () => {
        // Simply return all events from config - no date filtering
        return events;
    };

    const renderVideoStage = () => (
        <Box sx={{ 
            position: 'relative',
            width: '100%',
            maxWidth: '400px',
            aspectRatio: '608/1080',
            borderRadius: 2,
            overflow: 'hidden',
            mx: 'auto',
            // Ensure the container doesn't exceed viewport on mobile
            maxHeight: { xs: '80vh', sm: 'none' }
        }}>

            {/* Video Element */}
            <video
                ref={videoRef}
                width="100%"
                height="100%"
                poster={`${process.env.PUBLIC_URL}/assets/images/Headofschool-Intro-poster.jpg`}
                preload="metadata"
                playsInline
                webkit-playsinline="true"
                onEnded={handleVideoEnded}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onCanPlay={() => {}}
                onPlay={() => {
                    setIsPlaying(true);
                    setCooldown();
                }}
                onPause={() => setIsPlaying(false)}
                style={{ 
                    display: 'block', 
                    objectFit: 'cover',
                    width: '100%',
                    height: '100%'
                }}
            >
                <source src={`${process.env.PUBLIC_URL}/assets/videos/Headofschool-Intro.mp4`} type="video/mp4" />
                Your browser does not support the video tag.
            </video>

            {/* Large Center Play Button */}
            {!isPlaying && (
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2
                }}>
                    <IconButton
                        onClick={handlePlayPause}
                        sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            width: 80,
                            height: 80,
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 1)',
                                transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <PlayArrow sx={{ fontSize: 48, color: 'primary.main' }} />
                    </IconButton>
                </Box>
            )}

            {/* Custom Controls Bar */}
            <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                background: 'linear-gradient(rgba(0,0,0,0.7), transparent)',
                p: 2,
                opacity: showControls ? 1 : 0,
                transition: 'opacity 0.3s ease'
            }}>
                {/* Progress Bar */}
                <Box sx={{ mb: 1 }}>
                    <Slider
                        size="small"
                        value={duration > 0 ? (currentTime / duration) * 100 : 0}
                        onChange={handleSeek}
                        sx={{
                            color: '#fff',
                            '& .MuiSlider-thumb': {
                                width: 16,
                                height: 16,
                            },
                            '& .MuiSlider-track': {
                                backgroundColor: '#fff',
                            },
                            '& .MuiSlider-rail': {
                                backgroundColor: 'rgba(255,255,255,0.3)',
                            }
                        }}
                    />
                </Box>

                {/* Control Buttons Row */}
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between' 
                }}>
                    {/* Play/Pause and Time */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton 
                            onClick={handlePlayPause}
                            sx={{ color: '#fff', mr: 1 }}
                        >
                            {isPlaying ? <Pause /> : <PlayArrow />}
                        </IconButton>
                        <Typography variant="body2" sx={{ color: '#fff', minWidth: '80px' }}>
                            {formatTime(currentTime)} / {formatTime(duration)}
                        </Typography>
                    </Box>

                    {/* Volume Controls */}
                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: '120px' }}>
                        <IconButton 
                            onClick={handleMuteToggle}
                            sx={{ color: '#fff', mr: 1 }}
                        >
                            {isMuted ? <VolumeOff /> : <VolumeUp />}
                        </IconButton>
                        <Slider
                            size="small"
                            value={isMuted ? 0 : volume * 100}
                            onChange={handleVolumeChange}
                            sx={{
                                width: 80,
                                color: '#fff',
                                '& .MuiSlider-thumb': {
                                    width: 12,
                                    height: 12,
                                },
                                '& .MuiSlider-track': {
                                    backgroundColor: '#fff',
                                },
                                '& .MuiSlider-rail': {
                                    backgroundColor: 'rgba(255,255,255,0.3)',
                                }
                            }}
                        />
                    </Box>
                </Box>
            </Box>
        </Box>
    );

    const renderOpenHouseList = () => {
        const upcomingEvents = getUpcomingEvents();
        
        return (
            <Fade in={currentStage === MODAL_STAGES.OPEN_HOUSE_LIST} timeout={500}>
                <Box sx={{ width: '100%', maxWidth: '600px' }}>
                    <Typography variant="h5" component="h2" gutterBottom sx={{
                        mb: 3,
                        color: 'primary.dark',
                        textAlign: 'center',
                        px: 6, // Add padding on both sides to keep centered while avoiding close button
                        mt: 1  // Add small margin-top for extra spacing
                    }}>
                        Join Our Next Open House
                    </Typography>
                    
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Chip
                            label={`All times listed in ${timeZone}`}
                            color="primary"
                            variant="outlined"
                            icon={<AccessTime />}
                        />
                    </Box>

                    <Grid container spacing={2} sx={{ mb: 4, justifyContent: 'center' }}>
                        {events.length === 0 ? (
                            <Grid size={12}>
                                <Typography variant="body1" sx={{ textAlign: 'center', mt: 4 }}>
                                    Loading open house events...
                                </Typography>
                            </Grid>
                        ) : (upcomingEvents.length > 0 ? upcomingEvents : events).map((event, index) => (
                            <Grid size={{ xs: 12, sm: 6 }} key={index}>
                                <Card sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    elevation: 3,
                                    backgroundColor: '#fafafa',
                                    border: '1px solid #e0e0e0',
                                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: 6,
                                        backgroundColor: '#f5f5f5'
                                    }
                                }}>
                                    <CardContent sx={{
                                        p: 2.5,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                        flexGrow: 1
                                    }}>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mb: 1.5,
                                            color: 'primary.main'
                                        }}>
                                            <CalendarToday sx={{ fontSize: '1.3rem' }} />
                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                {event.date}
                                            </Typography>
                                        </Box>

                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mb: 2.5,
                                            color: 'text.secondary'
                                        }}>
                                            <AccessTime sx={{ fontSize: '1.1rem' }} />
                                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                                {event.time}
                                            </Typography>
                                        </Box>

                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            size="medium"
                                            onClick={() => handleRegisterClick(event)}
                                            sx={{
                                                px: 3,
                                                py: 1,
                                                fontSize: '0.9rem',
                                                fontWeight: 'regular',
                                                borderRadius: 2,
                                                mt: 'auto',
                                            }}
                                        >
                                            Register
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>


                    {events.length > 0 && (
                        <Box sx={{ textAlign: 'center', mt: 3 }}>
                            <Box sx={{ mb: 2 }}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => {
                                        handleClose();
                                        navigate('/open-house');
                                    }}
                                    sx={{ mr: 2, mb: 1 }}
                                >
                                    View All Open House Details
                                </Button>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Can't make these times? Contact us at{' '}
                                <Typography
                                    component="a"
                                    href={`mailto:${openHouseEmail}`}
                                    sx={{
                                        fontWeight: 'bold',
                                        color: 'primary.main',
                                        textDecoration: 'none',
                                        '&:hover': {
                                            textDecoration: 'underline'
                                        }
                                    }}
                                >
                                    {openHouseEmail}
                                </Typography>
                                {' '}to schedule a virtual meeting.
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Fade>
        );
    };

    const renderRegistrationForm = () => (
        <Fade in={currentStage === MODAL_STAGES.REGISTRATION_FORM} timeout={500}>
            <Box sx={{ width: '100%', maxWidth: '500px' }}>
                <OpenHouseRegistrationForm
                    selectedEvent={selectedEvent}
                    onCancel={handleBackToList}
                    onSuccess={handleRegistrationSuccess}
                />
            </Box>
        </Fade>
    );

    const getCurrentContent = () => {
        switch (currentStage) {
            case MODAL_STAGES.VIDEO:
                return renderVideoStage();
            case MODAL_STAGES.OPEN_HOUSE_LIST:
                return renderOpenHouseList();
            case MODAL_STAGES.REGISTRATION_FORM:
                return renderRegistrationForm();
            default:
                return renderVideoStage();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={() => {}}
            disableEscapeKeyDown
            maxWidth={false}
            slotProps={{
                backdrop: {
                    onClick: (e) => {
                        e.stopPropagation();
                    }
                }
            }}
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                    maxHeight: currentStage === MODAL_STAGES.VIDEO ? 
                        { xs: 'auto', sm: '90vh' } : '90vh',
                    overflow: 'visible',
                    width: currentStage === MODAL_STAGES.VIDEO ? 
                        { xs: '95vw', sm: '500px', md: '600px' } : 
                        { xs: '95vw', sm: '600px', md: '700px' },
                    maxWidth: 'none'
                }
            }}
            sx={{
                '& .MuiDialog-container': {
                    alignItems: 'center',
                    justifyContent: 'center'
                }
            }}
        >
            <Box sx={{ 
                position: 'absolute', 
                top: 8, 
                right: 8, 
                zIndex: 1 
            }}>
                <IconButton
                    onClick={handleClose}
                    sx={currentStage === MODAL_STAGES.VIDEO ? {
                        // White background for video stage (button over video content)
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 1)'
                        }
                    } : {
                        // No background for other stages (button over white dialog)
                        color: 'text.secondary',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            color: 'text.primary'
                        }
                    }}
                >
                    <Close />
                </IconButton>
            </Box>
            
            <DialogContent sx={{ 
                p: { xs: 2, sm: 4 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: currentStage === MODAL_STAGES.VIDEO ? 'center' : 'flex-start'
            }}>
                {getCurrentContent()}
            </DialogContent>
        </Dialog>
    );
};

export const shouldShowVideoModal = (cooldownDays = 7) => {
    const lastShown = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!lastShown) {
        return true;
    }
    
    const lastShownDate = new Date(parseInt(lastShown));
    const now = new Date();
    const daysSinceLastShown = (now - lastShownDate) / (1000 * 60 * 60 * 24);
    
    return daysSinceLastShown >= cooldownDays;
};

export const resetVideoModalCooldown = () => {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    console.log('Video modal cooldown reset. Refresh page to see modal again.');
};

// For testing purposes - add to browser console: window.resetVideoModal()
if (typeof window !== 'undefined') {
    window.resetVideoModal = resetVideoModalCooldown;
    
    // Mobile-friendly reset: Check for URL parameter on page load
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('resetVideo') === 'true') {
        resetVideoModalCooldown();
        // Clean up the URL parameter
        urlParams.delete('resetVideo');
        const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
        window.history.replaceState({}, '', newUrl);
    }
}

export default VideoModal;